// src/sections/SoftwareCompanies.js
import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

export default function SoftwareCompanies() {
  return (
    <React.Fragment>
      {/* SEO */}
      <Helmet>
        <title>Reflex Advisor | Lead Generation for Software Companies</title>
        <meta
          name="description"
          content="We find companies that need what you sell, contact them every day, and book meetings on your calendar. Reflex Advisor for Software Companies."
        />
        <meta
          name="keywords"
          content="software lead generation, B2B sales automation, software company growth, appointment booking, sales outreach"
        />
      </Helmet>

      {/* HERO SECTION */}
      <section className="sc-hero-section">
        <div className="sc-hero-inner">
          <div className="sc-hero-tag">Reflex Advisor</div>
          <div className="sc-hero-badge">Built For Software Companies</div>

          <h1 className="sc-hero-heading">
            We Get You <span className="sc-hero-highlight">Paying Clients.</span>
          </h1>

          <p className="sc-hero-description">
            We find companies that need what you sell, contact them every day,
            and book meetings on your calendar. You just show up and close.
          </p>

          <div className="sc-hero-cta">
            <Link to="/booking" className="sc-cta-button">
              Claim My Free Strategy Call
            </Link>
          </div>
        </div>

        <style>{`
          .sc-hero-section {
            background: #ffffff;
            color: #0a0a0a;
            padding: 96px 32px 80px;
            text-align: center;
            font-family: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            box-sizing: border-box;
          }
          .sc-hero-inner {
            max-width: 900px;
            margin: 0 auto;
          }
          .sc-hero-tag {
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: #ff3b30;
            margin-bottom: 16px;
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }
          .sc-hero-tag::before {
            content: '';
            display: inline-block;
            width: 6px;
            height: 6px;
            background: #ff3b30;
            border-radius: 50%;
          }
          .sc-hero-badge {
            display: inline-block;
            background: #0a0a0a;
            color: #ffffff;
            padding: 10px 16px;
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            margin-bottom: 32px;
            border-radius: 4px;
            margin-left: 16px;
          }
          .sc-hero-heading {
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 52px;
            font-weight: 700;
            line-height: 1.1;
            letter-spacing: -0.03em;
            color: #0a0a0a;
            margin: 0 0 24px;
            max-width: 720px;
            margin-left: auto;
            margin-right: auto;
          }
          .sc-hero-highlight {
            color: #ff3b30;
          }
          .sc-hero-description {
            font-family: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 18px;
            font-weight: 400;
            line-height: 1.65;
            color: rgba(0, 0, 0, 0.72);
            max-width: 520px;
            margin: 0 auto 52px;
            letter-spacing: 0.01em;
          }
          .sc-hero-cta {
            margin-top: 36px;
          }
          .sc-cta-button {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 14px 32px;
            background: #0a0a0a;
            color: #ffffff;
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 16px;
            font-weight: 600;
            border-radius: 980px;
            text-decoration: none;
            transition: background 0.25s ease, transform 0.2s ease, box-shadow 0.25s ease;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
          }
          .sc-cta-button:hover {
            background: #2a2a2a;
            transform: translateY(-2px);
            box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
          }
          @media (max-width: 1024px) {
            .sc-hero-heading { font-size: 42px; }
          }
          @media (max-width: 768px) {
            .sc-hero-section { padding: 80px 24px 60px; }
            .sc-hero-heading { font-size: 32px; letter-spacing: -0.02em; }
            .sc-hero-description { font-size: 16px; }
            .sc-hero-badge { margin-left: 0; display: block; margin-bottom: 24px; }
          }
          @media (max-width: 480px) {
            .sc-hero-heading { font-size: 26px; }
            .sc-hero-description { font-size: 15px; }
            .sc-cta-button { width: 100%; justify-content: center; padding: 12px 24px; font-size: 15px; }
            .sc-hero-badge { font-size: 10px; padding: 8px 12px; }
          }
        `}</style>
      </section>

      {/* STEPS SECTION */}
      <section className="sc-steps-section">
        <div className="sc-steps-inner">
          <div className="sc-steps-grid">
            <div className="sc-step">
              <div className="sc-step-label">Step 01</div>
              <h3 className="sc-step-title">We Find Them</h3>
              <p className="sc-step-desc">
                We research and build a list of companies that are a perfect fit
                for your software.
              </p>
            </div>
            <div className="sc-step">
              <div className="sc-step-label">Step 02</div>
              <h3 className="sc-step-title">We Contact Them</h3>
              <p className="sc-step-desc">
                SMS, email, and voice outreach runs every day. We follow up
                automatically until they respond.
              </p>
            </div>
            <div className="sc-step">
              <div className="sc-step-label">Step 03</div>
              <h3 className="sc-step-title">You Get The Meeting</h3>
              <p className="sc-step-desc">
                A booked appointment lands on your calendar. You show up and
                close the deal.
              </p>
            </div>
          </div>
        </div>

        <style>{`
          .sc-steps-section {
            background: #ffffff;
            padding: 80px 32px;
            font-family: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            box-sizing: border-box;
            border-top: 1.5px solid #0a0a0a;
          }
          .sc-steps-inner {
            max-width: 1200px;
            margin: 0 auto;
          }
          .sc-steps-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 56px;
          }
          .sc-step {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }
          .sc-step-label {
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: #ff3b30;
          }
          .sc-step-title {
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 22px;
            font-weight: 800;
            letter-spacing: -0.035em;
            line-height: 1.08;
            color: #0a0a0a;
            margin: 0;
          }
          .sc-step-desc {
            font-family: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 16px;
            font-weight: 400;
            color: rgba(0, 0, 0, 0.72);
            line-height: 1.6;
            margin: 0;
          }
          @media (max-width: 768px) {
            .sc-steps-section { padding: 60px 24px; }
            .sc-steps-grid { gap: 40px; }
          }
          @media (max-width: 480px) {
            .sc-steps-section { padding: 48px 20px; }
            .sc-steps-grid { gap: 32px; }
            .sc-step-title { font-size: 18px; }
            .sc-step-desc { font-size: 14px; }
          }
        `}</style>
      </section>

      {/* PROBLEMS SECTION */}
      <section className="sc-problems-section">
        <div className="sc-problems-inner">
          <div className="sc-problems-grid">
            <div className="sc-problem">
              <h3 className="sc-problem-title">You launched but nobody came.</h3>
              <p className="sc-problem-desc">
                Waiting for referrals is not a plan. We go out and get clients
                for you — starting this week.
              </p>
            </div>
            <div className="sc-problem">
              <h3 className="sc-problem-title">You have no time for outreach.</h3>
              <p className="sc-problem-desc">
                You are busy building the product. We handle every single step
                of the sales process for you.
              </p>
            </div>
          </div>
        </div>

        <style>{`
          .sc-problems-section {
            background: #ffffff;
            padding: 80px 32px;
            font-family: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            box-sizing: border-box;
            border-top: 1.5px solid #0a0a0a;
          }
          .sc-problems-inner {
            max-width: 1200px;
            margin: 0 auto;
          }
          .sc-problems-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 56px;
          }
          .sc-problem {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }
          .sc-problem-title {
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 22px;
            font-weight: 800;
            letter-spacing: -0.035em;
            line-height: 1.08;
            color: #0a0a0a;
            margin: 0;
          }
          .sc-problem-desc {
            font-family: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 16px;
            font-weight: 400;
            color: rgba(0, 0, 0, 0.72);
            line-height: 1.6;
            margin: 0;
          }
          @media (max-width: 768px) {
            .sc-problems-section { padding: 60px 24px; }
            .sc-problems-grid { gap: 40px; }
          }
          @media (max-width: 480px) {
            .sc-problems-section { padding: 48px 20px; }
            .sc-problems-grid { gap: 32px; grid-template-columns: 1fr; }
            .sc-problem-title { font-size: 18px; }
            .sc-problem-desc { font-size: 14px; }
          }
        `}</style>
      </section>

      {/* UPDATES SECTION */}
      <section className="sc-updates-section">
        <div className="sc-updates-inner">
          <h2 className="sc-updates-title">You get a full update every single day.</h2>
          <p className="sc-updates-desc">
            Voice recording and written summary of every outreach action, every
            response, and every booked meeting — sent to you daily.
          </p>
        </div>

        <style>{`
          .sc-updates-section {
            background: #ffffff;
            padding: 80px 32px;
            font-family: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            box-sizing: border-box;
            border-top: 1.5px solid #0a0a0a;
            text-align: center;
          }
          .sc-updates-inner {
            max-width: 800px;
            margin: 0 auto;
          }
          .sc-updates-title {
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 32px;
            font-weight: 800;
            letter-spacing: -0.035em;
            line-height: 1.1;
            color: #0a0a0a;
            margin: 0 0 24px;
          }
          .sc-updates-desc {
            font-family: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 18px;
            font-weight: 400;
            color: rgba(0, 0, 0, 0.72);
            line-height: 1.7;
            margin: 0;
          }
          @media (max-width: 768px) {
            .sc-updates-section { padding: 60px 24px; }
            .sc-updates-title { font-size: 26px; }
            .sc-updates-desc { font-size: 16px; }
          }
          @media (max-width: 480px) {
            .sc-updates-section { padding: 48px 20px; }
            .sc-updates-title { font-size: 20px; }
            .sc-updates-desc { font-size: 15px; }
          }
        `}</style>
      </section>

      {/* FEATURES SECTION */}
      <section className="sc-features-section">
        <div className="sc-features-inner">
          <div className="sc-features-grid">
            <div className="sc-feature">
              <div className="sc-feature-label">Daily</div>
              <div className="sc-feature-title">Outreach Running</div>
            </div>
            <div className="sc-feature">
              <div className="sc-feature-label">Full</div>
              <div className="sc-feature-title">Sales Engine Built</div>
            </div>
            <div className="sc-feature">
              <div className="sc-feature-label">3</div>
              <div className="sc-feature-title">Companies Per City Max</div>
            </div>
          </div>
        </div>

        <style>{`
          .sc-features-section {
            background: #ffffff;
            padding: 80px 32px;
            font-family: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            box-sizing: border-box;
            border-top: 1.5px solid #0a0a0a;
          }
          .sc-features-inner {
            max-width: 1200px;
            margin: 0 auto;
          }
          .sc-features-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 48px;
          }
          .sc-feature {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .sc-feature-label {
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 14px;
            font-weight: 600;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: rgba(0, 0, 0, 0.72);
          }
          .sc-feature-title {
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 20px;
            font-weight: 700;
            letter-spacing: -0.02em;
            color: #0a0a0a;
          }
          @media (max-width: 768px) {
            .sc-features-section { padding: 60px 24px; }
            .sc-features-grid { gap: 40px; }
          }
          @media (max-width: 480px) {
            .sc-features-section { padding: 48px 20px; }
            .sc-features-grid { gap: 32px; grid-template-columns: 1fr; }
            .sc-feature-title { font-size: 18px; }
          }
        `}</style>
      </section>

      {/* CLOSING SECTION */}
      <section className="sc-closing-section">
        <div className="sc-closing-inner">
          <h2 className="sc-closing-title">Only 3 software companies per city.</h2>
          <p className="sc-closing-desc">
            Once your city is taken, the door closes. Apply to check if your
            spot is open.
          </p>
          <div className="sc-closing-cta">
            <Link to="/booking" className="sc-closing-button">
              Apply Now →
            </Link>
          </div>
        </div>

        <style>{`
          .sc-closing-section {
            background: #ffffff;
            padding: 100px 32px 120px;
            font-family: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            box-sizing: border-box;
            border-top: 1.5px solid #0a0a0a;
            text-align: center;
          }
          .sc-closing-inner {
            max-width: 800px;
            margin: 0 auto;
          }
          .sc-closing-title {
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 36px;
            font-weight: 800;
            letter-spacing: -0.035em;
            line-height: 1.1;
            color: #0a0a0a;
            margin: 0 0 16px;
          }
          .sc-closing-desc {
            font-family: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 18px;
            font-weight: 400;
            color: rgba(0, 0, 0, 0.72);
            line-height: 1.7;
            margin: 0 0 40px;
          }
          .sc-closing-cta {
            margin-top: 32px;
          }
          .sc-closing-button {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 14px 32px;
            background: #ff3b30;
            color: #ffffff;
            font-family: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            font-size: 16px;
            font-weight: 600;
            border-radius: 980px;
            text-decoration: none;
            transition: background 0.25s ease, transform 0.2s ease, box-shadow 0.25s ease;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
          }
          .sc-closing-button:hover {
            background: #ff5247;
            transform: translateY(-2px);
            box-shadow: 0 8px 28px rgba(255, 59, 48, 0.35);
          }
          @media (max-width: 768px) {
            .sc-closing-section { padding: 80px 24px 100px; }
            .sc-closing-title { font-size: 28px; }
            .sc-closing-desc { font-size: 16px; }
          }
          @media (max-width: 480px) {
            .sc-closing-section { padding: 60px 20px 80px; }
            .sc-closing-title { font-size: 22px; }
            .sc-closing-desc { font-size: 15px; }
            .sc-closing-button { width: 100%; justify-content: center; padding: 12px 24px; font-size: 15px; }
          }
        `}</style>
      </section>
    </React.Fragment>
  );
}
